"use client";

import React, { useState } from "react";
import { Search, Share2, PenTool, ChartBar, ChevronDown, Zap, ArrowRight } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const disciplines = [{
  title: "Residential Services",
  icon: Share2,
  tags: ["Home Rewiring", "Lighting Upgrades", "Ceiling Fans", "Pool Electrical"],
  philosophy: "Your home's safety is our family's priority. From intricate troubleshooting to complete rewiring, we treat every residence with the precision and care of our own, ensuring your sanctuary is powered safely.",
  caseStudy: {
    metric: "1,200+",
    label: "Homes Served Since 2013",
    desc: "Providing reliable residential electrical solutions to families across the Richmond and Sugar Land areas."
  }
}, {
  title: "Commercial Solutions",
  icon: PenTool,
  tags: ["Build-Outs", "Maintenance", "Code Compliance", "Panel Upgrades"],
  philosophy: "Powering the engines of local business. We deliver robust electrical infrastructure that meets the heavy demands of commercial operations, minimizing downtime and maximizing operational efficiency.",
  caseStudy: {
    metric: "150+",
    label: "Corporate Facilities",
    desc: "Successful execution of large-scale electrical installations for retail, office, and industrial spaces."
  }
}, {
  title: "Safety & Inspections",
  icon: Search,
  tags: ["Wiring Audits", "Surge Protection", "GFCI Testing", "Panel Inspections"],
  philosophy: "Prevention is the highest form of protection. We conduct rigorous technical audits of your electrical system to identify vulnerabilities before they become hazards, keeping your property up to modern code.",
  caseStudy: {
    metric: "100%",
    label: "Safety Compliance",
    desc: "Achieved through comprehensive wiring inspections and proactive panel upgrades for aging properties."
  }
}, {
  title: "Emergency & Specialty",
  icon: ChartBar,
  tags: ["24/7 Service", "Generators", "EV Chargers", "Troubleshooting"],
  philosophy: "Available when you need us most. Whether it's a Level 2 EV charger installation or a midnight power outage, our rapid-response team is equipped to handle critical electrical failures at any hour.",
  caseStudy: {
    metric: "<60m",
    label: "Response Time",
    desc: "Average arrival time for emergency service calls in the Missouri City and surrounding Houston areas."
  }
}];
export default function CoreDisciplines() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="9226" id="powering-your-property" ref={ref} className={cn("bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-destructive/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-6">
            Powering Your <span className="text-primary">Property</span>
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed font-sans">
            Specialized electrical solutions for Richmond's residential and commercial landscapes. We deploy expert technicians to build and maintain safe, efficient digital and electrical ecosystems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {disciplines.map((item, i) => <DisciplineCard key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}
function DisciplineCard({
  item,
  index
}: {
  item: typeof disciplines[0];
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div data-index={index} className="h-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="h-full">
        <Card className={cn("h-full flex flex-col border-border bg-card/50 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:shadow-lg rounded-sm group overflow-hidden", isOpen ? "border-primary ring-1 ring-primary/20 bg-card" : "")}>
          <CardHeader className="pb-4 relative">
            {}
            <div className={cn("absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 transition-opacity duration-500", isOpen ? "opacity-100" : "group-hover:opacity-50")} />
            
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-sm bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <item.icon className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-border bg-muted/30">
                DIV {index + 1}
              </Badge>
            </div>
            <CardTitle className="text-2xl font-bold group-hover:text-primary transition-colors duration-300 tracking-tight">
              {item.title}
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-grow space-y-6">
            <div className="flex flex-wrap gap-2">
              {item.tags.map((tag, tIndex) => <Badge key={tIndex} variant="secondary" className="bg-muted text-foreground/80 hover:bg-primary/10 hover:text-primary border-0 transition-colors rounded-sm text-[10px] font-mono uppercase">
                  {tag}
                </Badge>)}
            </div>
            
            <p className="text-muted-foreground leading-relaxed text-sm border-l-2 border-destructive/30 pl-4 font-sans">
              {item.philosophy}
            </p>
          </CardContent>

          <CardFooter className="pt-2 pb-6 px-6 mt-auto flex flex-col items-stretch gap-4">
            <CollapsibleContent className="space-y-4 overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <div className="rounded-sm bg-muted/40 border border-border p-4 mt-2 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3 text-xs font-bold text-destructive uppercase tracking-widest font-mono">
                  <Zap className="w-3 h-3" /> Performance Data
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-bold text-foreground tracking-tighter">{item.caseStudy.metric}</span>
                </div>
                <div className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">{item.caseStudy.label}</div>
                <p className="text-xs text-muted-foreground leading-snug">
                  {item.caseStudy.desc}
                </p>
              </div>
            </CollapsibleContent>

            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between group/btn hover:bg-primary/5 hover:text-primary border border-transparent hover:border-primary/10 rounded-sm">
                <span className="text-sm font-bold uppercase tracking-wider font-mono">
                  {isOpen ? "Hide Details" : "Service Insights"}
                </span>
                <ChevronDown className={cn("w-4 h-4 transition-transform duration-300", isOpen ? "rotate-180" : "group-hover/btn:translate-y-0.5")} />
              </Button>
            </CollapsibleTrigger>
          </CardFooter>
        </Card>
      </Collapsible>
    </div>;
}